import type {SvgIconComponent} from "@mui/icons-material";
import GridViewIcon from "@mui/icons-material/GridView";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SportsFootballIcon from "@mui/icons-material/SportsFootball";
import StyleIcon from "@mui/icons-material/Style";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import SportsMmaIcon from "@mui/icons-material/SportsMma";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball";
import GolfCourseIcon from "@mui/icons-material/GolfCourse";
import SportsHockeyIcon from "@mui/icons-material/SportsHockey";
import SportsKabaddiIcon from "@mui/icons-material/SportsKabaddi";
import SportsIcon from "@mui/icons-material/Sports";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";

export type CategoryIconConfig = {
    Icon: SvgIconComponent;
};

type CategoryIconGroupConfig = {
    Icon: SvgIconComponent;
    categories: Record<string, Partial<CategoryIconConfig>>;
};

type CategoryIconsConfig = Record<string, CategoryIconGroupConfig>;

export const DEFAULT_CATEGORY_ICON_KEY = "__default__";

export const normalizeCategoryIconKey = (value: string) => value.trim().toLowerCase();

const createCategoryIconsByName = (
    icons: CategoryIconsConfig,
    DefaultIcon?: SvgIconComponent
): Record<string, CategoryIconConfig> => {
    const result: Record<string, CategoryIconConfig> = {};

    if (DefaultIcon) {
        result[DEFAULT_CATEGORY_ICON_KEY] = {
            Icon: DefaultIcon,
        };
    }

    Object.values(icons).forEach((value) => {
        Object.entries(value.categories).forEach(([categoryKey, categoryValue]) => {
            result[normalizeCategoryIconKey(categoryKey)] = {
                Icon: categoryValue.Icon ?? value.Icon,
            };
        });
    });

    return result;
};

const SPORTS_CATEGORIES_ICONS_CONFIG: CategoryIconsConfig = {
    GENERAL: {
        Icon: GridViewIcon,
        categories: {
            "ALL": {},
            "TODOS": {},
        },
    },
    LIVE: {
        Icon: PlayCircleIcon,
        categories: {
            "LIVE NOW": {},
        },
    },
    BASEBALL: {
        Icon: SportsBaseballIcon,
        categories: {
            "LMB": {},
            "MLB": {},
            "NCAA Beisbol": {},
        },
    },
    TOURNAMENTS: {
        Icon: EmojiEventsIcon,
        categories: {
            "CHAMPIONS LEAGUE": {},
            "LEAGUES CUP": {},
            "UEFA CHAMPIONS LEAGUE": {},
            "UEFA EUROPA LEAGUE": {},
            "UEFA EUROPA CONFERENCE": {},
            "CONCACAF CHAMPIONS CUP": {},
            "CONMEBOL LIBERTADORES": {},
            "CONMEBOL SUDAMERICANA": {},
            "EFL Cup": {},
            "UEFA Europa Conference League": {},
            "CONCACAF": {},
            "CONMEBOL Nations League": {},
            "DFB Pokal": {},
            "COPA DE FRANCIA": {},
            "CONCACAF Caribbean Club": {},
            "Copa Sudamericana": {},
            "COPPA ITALIA": {},
            "FOOTBALL CONMEBOL Libertadores": {},
        },
    },
    AMERICAN_FOOTBALL: {
        Icon: SportsFootballIcon,
        categories: {
            "NCAAF": {},
            "NFL": {},
        },
    },
    POKER: {
        Icon: StyleIcon,
        categories: {
            "POKER": {},
        },
    },
    E_GAMES: {
        Icon: VideogameAssetIcon,
        categories: {
            "E-GAMES": {},
        },
    },
    SOCCER: {
        Icon: SportsSoccerIcon,
        categories: {
            "FIFA": {},
            "LIGAS MX": {},
            "PREMIER LEAGUE": {},
            "LALIGA": {},
            "FOOTBALL LIGAS MX": {},
            "SERIE A": {},
            "BUNDESLIGA": {},
            "LIGUE 1": {},
            "MLS": {},
            "FOOTBALL AMERICAS": {},
            "SUPER LIG": {},
            "PRO LEAGUE": {},
            "FOOTBALL INTERNACIONAL": {},
            "LIGA MX (FEMENIL)": {},
            "CHAMPIONSHIP EFL": {},
            "INTERNACIONAL": {},
            "FOOTBALL FRIENDLIES": {},
            "FOOTBAALL INTERNACIONAL": {},
            "USL Championship": {},
            "FOOTBALL USA/CANADA": {},
            "UEFA International Friendlies": {},
            "EFL Championship": {},
            "BUNDESLIGA 2": {},
            "SAUDI PRO LEAGUE": {},
            "Arabia Pro League": {},
            "League One": {},
            "CLUB FRIENDLIES": {},
            "Liga de Expansión MX": {},
            "Amistosos internacionales": {},
            "FOOTBALL WORLD": {},
        },
    },
    MOTORSPORTS: {
        Icon: SportsScoreIcon,
        categories: {
            "FORMULA 1": {},
            "AUTOMOVILISMO": {},
        },
    },
    COMBAT_SPORTS: {
        Icon: SportsMmaIcon,
        categories: {
            "PELEAS": {},
            "ACTION SPORTS": {Icon: SportsKabaddiIcon},
        },
    },
    RACKET_SPORTS: {
        Icon: SportsTennisIcon,
        categories: {
            "PADEL": {},
            "TENNIS": {},
        },
    },
    BASKETBALL: {
        Icon: SportsBasketballIcon,
        categories: {
            "NBA": {},
            "WNBA": {},
            "FIBA": {},
            "NBA G League": {},
        },
    },
    GOLF: {
        Icon: GolfCourseIcon,
        categories: {
            "GOLF": {},
            "PGA TOUR": {},
            "DP World Tour": {},
        },
    },
    CYCLING: {
        Icon: DirectionsBikeIcon,
        categories: {
            "Cycling": {},
        },
    },
    HOCKEY: {
        Icon: SportsHockeyIcon,
        categories: {
            "NHL": {},
        },
    },
};

export const SPORTS_CATEGORIES_ICONS_BY_NAME: Record<string, CategoryIconConfig> = createCategoryIconsByName(
    SPORTS_CATEGORIES_ICONS_CONFIG,
    SportsIcon
);
